import { useEffect, useRef, useState } from 'react'
import Spinner from './Spinner'
import { loadThumbnails, loadFullThumbnails, type ThumbnailFrame } from '../lib/thumbnailLoader'
import type { ReplaySource } from './TaskEventReplayImpl'

type ThumbnailReplaySource = Exclude<ReplaySource, { recordingId: string }>

// Default thumbnail box, matching the previous rrweb preview. Callers can
// pass a larger box via the `width`/`height` props.
const DEFAULT_PREVIEW_W = 165
const DEFAULT_PREVIEW_H = 95
// Once the latest frame stops changing for this many consecutive polls, the run is treated as
// finished and polling stops (the thumbnail no longer needs refreshing).
const STABLE_POLLS_TO_STOP = 3
// How fast the hover preview advances through the available frames.
const HOVER_CYCLE_MS = 450

function sourceKey(source: ThumbnailReplaySource): { type: 'test_run' | 'exploration' | 'task'; id: string } {
  if ('testRunId' in source) return { type: 'test_run', id: source.testRunId }
  if ('explorationId' in source) return { type: 'exploration', id: source.explorationId }
  return { type: 'task', id: source.taskId }
}

// Compact, in-row preview built from server-stored thumbnail frames (JPEG screenshots the recording
// browser captures at rrweb-flush time; see scripts/event-stream.ts).
//
// To keep the grid fast, the polled request returns only the NEWEST frame per row (a page of rows is
// one small batched request). The full frame set — used for the hover progression cycle — is fetched
// lazily the first time a row is hovered, so the common case never ships ~10 base64 frames per row.
export default function RowReplayPreviewImpl({ source, pollMs = 5000, width = DEFAULT_PREVIEW_W, height = DEFAULT_PREVIEW_H, fit = 'contain', live = false }: { source: ThumbnailReplaySource; pollMs?: number; width?: number; height?: number; fit?: 'contain' | 'cover'; live?: boolean }) {
  const { type: sourceType, id: sourceId } = sourceKey(source)

  const [frames, setFrames] = useState<ThumbnailFrame[]>([]) // latest frame only (cheap poll)
  const [fullFrames, setFullFrames] = useState<ThumbnailFrame[] | null>(null) // all frames, hover-lazy
  const [fullLoading, setFullLoading] = useState(false) // hover full-frame download in flight
  const [loaded, setLoaded] = useState(false)
  const [active, setActive] = useState(true)
  const [index, setIndex] = useState(0)
  const [hovering, setHovering] = useState(false)

  const lastKeyRef = useRef('')
  const stableRef = useRef(0)

  // Poll the latest frame (batched across rows) while the run is active. Stops once it's been stable.
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const data = await loadThumbnails(sourceType, sourceId)
        if (cancelled) return
        setFrames(data)
        const key = data.map(f => f.url).join('|')
        if (key === lastKeyRef.current) {
          stableRef.current += 1
        } else {
          stableRef.current = 0
          lastKeyRef.current = key
        }
        // Stop polling only once the frame set has been stable for STABLE_POLLS_TO_STOP polls AND the
        // run is no longer live. While the run is live (queued/in-progress) — crucially including the
        // initial window before it has produced ANY frames — we keep polling so the first (and later)
        // thumbnails appear without a page refresh. (An empty frame set's key matches the initial ref,
        // so counting empty polls as "stable" used to shut the poller off ~15s in, freezing the card
        // on "No preview yet" until refresh.) Once not live, the stable-stop still bounds polling so a
        // run that never records doesn't poll forever.
        if (!live && stableRef.current >= STABLE_POLLS_TO_STOP) setActive(false)
      } catch {
        /* best-effort; a failed poll just leaves the current preview in place */
      } finally {
        if (!cancelled) setLoaded(true)
      }
    }
    void load()
    if (!active) return () => { cancelled = true }
    const id = setInterval(() => void load(), pollMs)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [sourceType, sourceId, pollMs, active, live])

  // Source changed → drop any stale hover frames so the next hover refetches.
  useEffect(() => {
    setFullFrames(null)
    setFullLoading(false)
    setHovering(false)
    setIndex(0)
  }, [sourceType, sourceId])

  // Lazily fetch the full frame set the first time the row is hovered (shows a spinner meanwhile).
  useEffect(() => {
    if (!hovering || fullFrames !== null) return
    let cancelled = false
    setFullLoading(true)
    loadFullThumbnails(sourceType, sourceId)
      .then(all => { if (!cancelled) setFullFrames(all) })
      .catch(() => { if (!cancelled) setFullFrames([]) })
      .finally(() => { if (!cancelled) setFullLoading(false) })
    return () => { cancelled = true }
  }, [hovering, fullFrames, sourceType, sourceId])

  // Reset the cursor when hover state or the loaded frame set changes.
  useEffect(() => { setIndex(0) }, [hovering, fullFrames])

  // While hovering a multi-frame run, cycle through the frames to show the journey's progression.
  useEffect(() => {
    const len = hovering && fullFrames ? fullFrames.length : 0
    if (len < 2) return
    const id = setInterval(() => setIndex(i => (i + 1) % len), HOVER_CYCLE_MS)
    return () => clearInterval(id)
  }, [hovering, fullFrames])

  // Render the cycled full set while hovering (once loaded), otherwise the latest frame.
  const activeFrames = hovering && fullFrames && fullFrames.length > 0 ? fullFrames : null
  const baseFrames = activeFrames ?? frames
  const hasFrames = baseFrames.length > 0
  const cursor = activeFrames ? index % activeFrames.length : baseFrames.length - 1
  const current = hasFrames ? baseFrames[Math.max(0, Math.min(cursor, baseFrames.length - 1))] : null

  return (
    <div
      className="row-preview__frame"
      style={{ width, height }}
      data-testid="row-preview-frame"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {current && (
        <img
          className="row-preview__img"
          src={current.url}
          alt="Journey preview"
          draggable={false}
          style={{ objectFit: fit === 'cover' ? 'cover' : 'contain' }}
        />
      )}
      {hovering && fullLoading && hasFrames && (
        <div className="row-preview__hover-spinner" data-testid="row-preview-hover-spinner" aria-hidden>
          <Spinner size={18} className="row-preview__spinner" />
        </div>
      )}
      {activeFrames && activeFrames.length > 1 && (
        <div className="row-preview__dots" aria-hidden>
          {activeFrames.map((f, i) => (
            <span key={f.frame_index} className={`row-preview__dot${i === cursor ? ' is-active' : ''}`} />
          ))}
        </div>
      )}
      {!hasFrames && (
        loaded ? (
          <div className="row-preview__empty" data-testid="row-preview-empty">No preview yet</div>
        ) : (
          <div className="row-preview__loading" data-testid="row-preview-loading">
            <Spinner size={16} className="row-preview__spinner" />
            <span>Loading…</span>
          </div>
        )
      )}
    </div>
  )
}
